import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiUpload } from '../api';

const Profile = ({ resellerID }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { profile } = await apiGet('/api/account/me');
      if (profile) { setProfileData(profile); setError(null); }
      else { setError("Reseller profile not found for the current user."); setProfileData(null); }
    } catch {
      setError("Failed to fetch profile data.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("File is too large. Please select an image under 2MB."); return; }
    if (!file.type.startsWith('image/')) { alert("Invalid file type. Please select an image."); return; }
    setUploading(true);
    try {
      const { url } = await apiUpload('resellers', file, `profile_${Date.now()}`);
      await apiPost('/api/account/profile-picture', { url });
      setProfileData(prev => ({ ...prev, profilePicture: url }));
      alert("Profile picture updated successfully!");
    } catch (err) {
      setError("Failed to upload image. Please try again.");
    } finally { setUploading(false); }
  };

  if (loading) return <div className="dark-loading"><div className="spinner" /><p>Loading Profile...</p></div>;
  if (error || !profileData) return <div className="dark-page"><div className="dark-error">{error || "No profile data available."}</div></div>;

  const infoRows = [
    { icon: 'fa-user-circle', label: 'Username', value: profileData.name },
    { icon: 'fa-envelope', label: 'Email', value: profileData.email },
    { icon: 'fa-id-badge', label: 'Reseller ID', value: profileData.ID },
  ];

  const financials = [
    { icon: 'fa-arrow-circle-down', label: 'Total Received', value: (profileData.Received || 0).toFixed(2), color: '#4ade80' },
    { icon: 'fa-arrow-circle-up', label: 'Total Sent', value: (profileData.Send || 0).toFixed(2), color: '#fb923c' },
    { icon: 'fa-wallet', label: 'Current Balance', value: (profileData.Balance || 0).toFixed(2), color: '#38bdf8' },
  ];

  return (
    <div className="dark-page" style={{ maxWidth: 600, margin: '0 auto' }}>
      {/* Avatar */}
      <div className="dark-card" style={{ textAlign: 'center', padding: '32px 20px', marginBottom: 16 }}>
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
          {profileData.profilePicture ? (
            <img src={profileData.profilePicture} alt="Profile" style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '3px solid #ff4d6d' }} />
          ) : (
            <div style={{ width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,77,109,0.15)', border: '3px solid #ff4d6d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-user" style={{ fontSize: 40, color: '#ff4d6d' }} />
            </div>
          )}
          <label htmlFor="profile-upload" style={{ position: 'absolute', bottom: -4, right: -4, width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#ff4d6d,#ff2d55)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px solid #0a0a1a' }}>
            <i className="fas fa-camera" style={{ fontSize: 13, color: '#fff' }} />
            <input id="profile-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} disabled={uploading} />
          </label>
        </div>
        {uploading && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Uploading...</p>}
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{profileData.name}</h2>
        <span className="badge badge-blue">Reseller</span>
      </div>

      {/* Account Details */}
      <div className="dark-card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.5)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Account Details</h3>
        {infoRows.map((row, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < infoRows.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>
              <i className={`fas ${row.icon}`} style={{ width: 16 }} />
              {row.label}
            </div>
            <span style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 14 }}>{row.value}</span>
          </div>
        ))}
      </div>

      {/* Financials */}
      <div className="dark-card">
        <h3 style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.5)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Financials</h3>
        {financials.map((row, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < financials.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>
              <i className={`fas ${row.icon}`} style={{ width: 16, color: row.color }} />
              {row.label}
            </div>
            <span style={{ fontWeight: 700, color: row.color, fontSize: 16 }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Profile;
